import './ui/scss/app.scss';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import 'react-toastify/dist/ReactToastify.css';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from 'react-router-dom';

import * as dayjs from 'dayjs';
import 'dayjs/locale/it';

import './ui/scss/bootstrap/bootstrap-grid.min.css';
import { toast, Zoom } from 'react-toastify';
import { useIdleTimer } from 'react-idle-timer';
import { HelmetProvider } from 'react-helmet-async';
import theme from './ui/theme';
import routes from './routes';
import { GlobalLayout } from './ui/components';

/* Pages */
import {
  Auth,
  AuthReset,
  Home,
  Search,
  Faq,
  Notifications,
  Maintenance,
  Bookmarks,
  AuthOTP,
  AuthSSO,
  UserStatistics,
  CourseStatistics,
} from './app/pages';
import {
  APP_STORE_RELOAD,
  AUTH_LOGOUT,
  SET_IS_ACTIVE,
} from './app/redux/actions';
import { eventBus, shouldUseMaintenance } from './utils/common';
import Toast, { ToastError } from './ui/components/Toast';
import CourseDetails from './app/pages/CourseDetails';
import CourseList from './app/pages/CourseList';
import storage from './app/api/storage';
import Dashboard from './app/pages/Dashboard';
import PublicPage from './app/pages/PublicPage';
import NewsDetails from './app/pages/NewsDetails';
import GlobalLayoutPublic from './ui/components/GlobalLayoutPublic';
import { MODULES } from './utils/modules';
import Documents from './app/pages/Documents';
import DocumentDetails from './app/pages/DocumentDetails';
import NewsletterDetails from './app/pages/NewsletterDetails';
import EditProfile from './app/pages/EditProfile';
import { AllIcons } from './ui/components/AllIcons';
import News from './app/pages/News';
import NotificationDetails from './app/pages/NotificationDetails';
import FullscreenIframe from './app/pages/FullscreenIframe';
import CreateCourse from './app/pages/CreateCourse';
import EditCourse from './app/pages/EditCourse';
import ScormList from './app/pages/ScormList';
import TutorCourseList from './app/pages/TutorCourseList';
import ModalExpiringSession from './ui/atoms/Modals/ModalExpiringSession';
import MyGroups from './app/pages/MyGroups';
import Tooltips from './ui/components/Tooltips';
import ModalDownloadReport from './ui/atoms/Modals/ModalDownloadReport';
import ChooseCourseType from './app/pages/ChooseCourseType';
import TutorStatistics from './app/pages/TutorStatistics';
import EditEvent from './app/pages/EditEvent';
import LiveEventList from './app/pages/LiveEventList';
import LiveEventDetails from './app/pages/LiveEventDetails';
import Statistics from './app/pages/Statistics';
import TutorStats from './app/pages/Statistics/TutorStats';
import SendNewsletter from './app/pages/SendNewsletter';
import DocumentsExpert from './app/pages/DocumentsExpert';
import DocumentExpertDetails from './app/pages/DocumentExpertDetails';
import ApprovalList from './app/pages/ApprovalList';
import LogsList from './app/pages/LogsList';
import PreviewEvent from './app/pages/PreviewEvent';
import Download from './app/pages/DownloadPdf';
import FullCatalog from './app/pages/FullCatalog';
import DownloadCatalog from './app/pages/DownloadCatalog';
import AuthSet from './app/pages/AuthSet';
import './style.css';
import { ToastContainer, ToastWrapper } from './ui/components/Toast/style';

const App = ({
  appStoreReload,
  isLogged,
  isTutor,
  isCotutor,
  isSupervisor,
  isSubjectExpert,
  modalIsOpen,
  logout,
  modules,
  setIsActive,
}) => {
  dayjs.locale('it');

  useEffect(() => {
    appStoreReload();
    const darkMode = false;
    // const darkMode = true;
    const themeName = darkMode ? 'darkTheme' : 'mainTheme';

    storage.write('theme', themeName);
    document.body.style = `background: ${theme(themeName).colors.bgPrimary};
    color: ${theme(themeName).colors.textPrimary};`;

    eventBus.on('logout', () => {
      logout();
      toast.error(<ToastError title="Sessione scaduta" />, {
        toastId: 'expiredToken',
      });
    });
    return () => {
      eventBus.remove('logout');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogged]);

  useEffect(() => {
    // toast.dismiss();
  }, [modalIsOpen]);

  const onIdle = () => setIsActive(false);
  const onActive = () => setIsActive(true);
  useIdleTimer({
    onIdle,
    onActive,
    onAction: () => {},
    timeout: 10 * 60 * 1000, // 10min
    throttle: 500,
  });

  // calc a dynamic version of the vw unit that accounts for presence of the scrollbar
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const vw = document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--vw', `${vw / 100}px`);
      document.documentElement.style.setProperty('--full-width-vw', `${vw}px`);
    });

    resizeObserver.observe(document.documentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Router>
      <AllIcons />
      <HelmetProvider>
        <ThemeProvider theme={theme(storage.read('theme')?.value)}>
          {shouldUseMaintenance() ? (
            <Routes>
              <Route
                path={`${routes.maintenance.path}`}
                element={<Maintenance />}
              />
              <Route
                path="*"
                element={<Navigate to={routes.maintenance.path} replace />}
              />
            </Routes>
          ) : (
            <Routes>
              {modules.includes(MODULES.PUBLIC_SITE) && (
                <Route
                  path={routes.public.path}
                  element={<GlobalLayoutPublic />}
                >
                  {/* NEWS */}
                  {modules.includes(MODULES.NEWS) && (
                    <>
                      <Route path={routes.publicNews.path} element={<News />} />
                      <Route>
                        <Route
                          path={`${routes.publicNewsDetails.path}/:news_id`}
                          element={<NewsDetails />}
                        />
                      </Route>
                    </>
                  )}
                  {/* FAQ */}
                  {modules.includes(MODULES.FAQ) && (
                    <Route path={routes.publicFaq.path} element={<Faq />} />
                  )}
                  <Route path={routes.public.path} element={<PublicPage />} />
                  <Route
                    path={`${routes.public.path}/*`}
                    element={<PublicPage />}
                  />
                </Route>
              )}
              {isLogged ? (
                <>
                  <Route path={routes.home.path} element={<GlobalLayout />}>
                    <>
                      <Route path={routes.home.path} element={<Home />} />
                      {/* COURSES */}
                      <Route
                        path={`${routes.fullCatalog.path}`}
                        element={<FullCatalog />}
                      />
                      <Route
                        path={`${routes.catalog.path}/*`}
                        element={<CourseList />}
                      />
                      <Route
                        path={`${routes.courseDetails.path}/:course_id`}
                        element={<CourseDetails />}
                      />
                      <Route
                        path={`${routes.courseDetails.path}/:course_id/:tab_name`}
                        element={<CourseDetails />}
                      />
                      <Route
                        path={`${routes.search.path}`}
                        element={<Search title={routes.search.title} />}
                      />
                      <Route
                        path={routes.dashboard.path}
                        element={<Dashboard />}
                      />
                      <Route
                        path={routes.bookmarks.path}
                        element={<Bookmarks />}
                      />
                      {/* NOTIFICATIONS */}
                      {modules.includes(MODULES.NOTIFICATIONS) && (
                        <>
                          <Route
                            path={routes.notifications.path}
                            element={<Notifications />}
                          />
                          <Route path={routes.notificationDetails.path}>
                            <Route
                              path=":notification_id"
                              element={<NotificationDetails />}
                            />
                          </Route>
                        </>
                      )}
                      {/* FAQ */}
                      {modules.includes(MODULES.FAQ) && (
                        <Route path={routes.faq.path} element={<Faq />} />
                      )}
                      {/* NEWS */}
                      {modules.includes(MODULES.NEWS) && (
                        <>
                          <Route path={routes.news.path} element={<News />} />
                          <Route
                            path={`${routes.newsDetails.path}/:news_id`}
                            element={<NewsDetails />}
                          />
                        </>
                      )}
                      {/* DOCUMENTS */}
                      {modules.includes(MODULES.DOCUMENTS) && (
                        <>
                          <Route
                            path={routes.documents.path}
                            element={<Documents />}
                          />
                          <Route path={routes.documentDetails.path}>
                            <Route
                              path=":document_id"
                              element={<DocumentDetails />}
                            />
                          </Route>
                        </>
                      )}
                      {/* NEWSLETTER */}
                      {modules.includes(MODULES.NEWSLETTER) && (
                        <Route path={routes.newsletterDetails.path}>
                          <Route
                            path=":newsletter_id"
                            element={<NewsletterDetails />}
                          />
                        </Route>
                      )}
                      {/* EDIT PROFILE */}
                      <Route
                        path={routes.editProfile.path}
                        element={<EditProfile />}
                      />
                      {/* TUTOR */}
                      {(typeof isTutor === 'undefined' ||
                        isTutor ||
                        isCotutor) && (
                        <>
                          {(typeof isTutor === 'undefined' || isTutor) && (
                            <>
                              <Route
                                path={routes.chooseCourseType.path}
                                element={<ChooseCourseType />}
                              />
                              <Route
                                path={routes.createCourse.path}
                                element={<CreateCourse />}
                              />
                              <Route
                                path={routes.createEvent.path}
                                element={<CreateCourse isEvent />}
                              />
                              <Route
                                path={routes.liveEventList.path}
                                element={<LiveEventList />}
                              />
                              <Route path={routes.liveEvent.path}>
                                <Route
                                  path=":event_id"
                                  element={<LiveEventDetails />}
                                />
                              </Route>
                              {/* STATISTICS */}
                              <Route
                                path={routes.statistics.path}
                                element={<Statistics />}
                              />
                              <Route path={routes.userStatistics.path}>
                                <Route
                                  path=":user_id"
                                  element={<UserStatistics />}
                                />
                              </Route>
                              <Route path={routes.courseStatistics.path}>
                                <Route
                                  path=":course_id/:tab_name"
                                  element={<CourseStatistics />}
                                />
                              </Route>
                              {/* GROUPS */}
                              <Route
                                path={routes.groupList.path}
                                element={<MyGroups />}
                              />
                            </>
                          )}
                          {/* ONLY COTUTORS */}
                          <Route path={routes.editCourse.path}>
                            <Route path=":course_id" element={<EditCourse />} />
                          </Route>
                          <Route
                            path={routes.tutorCourseList.path}
                            element={<TutorCourseList />}
                          />
                          <Route
                            path={routes.scormList.path}
                            element={<ScormList />}
                          />
                          <Route path={routes.editEvent.path}>
                            <Route path=":course_id" element={<EditEvent />} />
                          </Route>
                        </>
                      )}
                      {/* SUPERVISOR */}
                      {(typeof isSupervisor === 'undefined' ||
                        isSupervisor) && (
                        <>
                          <Route path={routes.tutorStatistics.path}>
                            <Route
                              path=":user_id"
                              element={<TutorStatistics />}
                            />
                          </Route>
                          <Route path={routes.tutorDashboard.path}>
                            <Route path=":user_id" element={<TutorStats />} />
                          </Route>
                          <Route
                            path={routes.sendNewsletter.path}
                            element={<SendNewsletter />}
                          />
                          <Route
                            path={routes.approvalList.path}
                            element={<ApprovalList />}
                          />
                          <Route path={routes.previewEvent.path}>
                            <Route
                              path=":event_id"
                              element={<PreviewEvent />}
                            />
                          </Route>
                          <Route
                            path={routes.logsList.path}
                            element={<LogsList />}
                          />
                        </>
                      )}
                      {/* SUBJECT EXPERT */}
                      {(typeof isSubjectExpert === 'undefined' ||
                        isSubjectExpert ||
                        isSupervisor) && (
                        <>
                          <Route
                            path={routes.documentsExpert.path}
                            element={<DocumentsExpert />}
                          />
                          <Route path={routes.documentExpertDetails.path}>
                            <Route
                              path=":document_id"
                              element={<DocumentExpertDetails />}
                            />
                          </Route>
                        </>
                      )}
                    </>
                  </Route>
                  <Route
                    path={`${routes.courseDetails.path}/:course_id/fullscreen`}
                    element={<FullscreenIframe />}
                  />
                  {(typeof isTutor === 'undefined' ||
                    typeof isSupervisor === 'undefined' ||
                    isTutor ||
                    isSupervisor) && (
                    <Route
                      path={routes.downloadPdf.path}
                      element={<Download />}
                    />
                  )}
                  <Route
                    path={routes.downloadCatalog.path}
                    element={<DownloadCatalog />}
                  />
                  <Route
                    path="*"
                    element={<Navigate to={routes.home.path} replace />}
                  />
                </>
              ) : (
                <>
                  {/* this route show authPage until app is reloaded and isLogged becomes true */}
                  <Route
                    path={routes.public.path}
                    element={<GlobalLayoutPublic />}
                  >
                    <Route path={routes.home.path} element={<Auth />} />
                  </Route>
                  <Route
                    path={routes.auth.path}
                    element={<GlobalLayoutPublic />}
                  >
                    {modules.includes(MODULES.AUTH) && (
                      <>
                        <Route path="" element={<Auth />} />
                        <Route
                          path={routes.resetAuth.path}
                          element={<AuthReset />}
                        />
                        <Route
                          path={routes.otpAuth.path}
                          element={<AuthOTP />}
                        />
                        <Route
                          path={routes.authSSO.path}
                          element={<AuthSSO />}
                        />
                        <Route
                          path={routes.authSet.path}
                          element={<AuthSet />}
                        />
                      </>
                    )}
                  </Route>
                  <Route
                    path="*"
                    element={<Navigate to={routes.public.path} replace />}
                  />
                </>
              )}
            </Routes>
          )}
          {/* <Loader /> */}
          {isLogged && <ModalExpiringSession />}
          {(isTutor || isSupervisor) && <ModalDownloadReport />}
          <Toast />
          <ToastWrapper>
            <ToastContainer
              autoClose={5000}
              closeButton
              zoom={Zoom}
              pauseOnHover
              draggable
              is
            />
          </ToastWrapper>
          <Tooltips />
        </ThemeProvider>
      </HelmetProvider>
    </Router>
  );
};

App.propTypes = {
  // HOC (connect, state)
  isLogged: PropTypes.bool.isRequired,
  isTutor: PropTypes.bool,
  isCotutor: PropTypes.bool,
  isSupervisor: PropTypes.bool,
  isSubjectExpert: PropTypes.bool,
  modules: PropTypes.array.isRequired,
  modalIsOpen: PropTypes.bool,

  // HOC (connect, dispatch)
  appStoreReload: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  setIsActive: PropTypes.func,
};

export default connect(
  (state) => {
    const { isLogged } = state.app.authState;
    const { isTutor, isCotutor, isSupervisor, isSubjectExpert } = state.user;
    const { modules } = state.modules;
    const { isOpen: modalIsOpen } = state.app.modal;

    return {
      isLogged,
      isTutor,
      isCotutor,
      isSupervisor,
      isSubjectExpert,
      modules,
      modalIsOpen,
    };
  },
  (dispatch) => ({
    appStoreReload: () => dispatch({ type: APP_STORE_RELOAD }),
    logout: () => dispatch({ type: AUTH_LOGOUT._REQUEST, skipTracking: true }),
    setIsActive: (action) => dispatch({ type: SET_IS_ACTIVE, action }),
  })
)(App);
